import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface UserDetail {
  user: {
    id: string; email: string; name: string | null; locale: string
    emailVerified: boolean; defaultTenantId: string | null; defaultRole: string | null
    lastSignInAt: string | null; createdAt: string; updatedAt: string
  }
  memberships: { id: string; role: string; tenant: { id: string; name: string; plan: string; status: string } }[]
}

export function AdminUserDetailPage() {
  const t = useT()
  const { id = '' } = useParams()
  const user = useQuery({ queryKey: ['admin-user', id], queryFn: () => apiClient.get<UserDetail>(`/admin/users/${id}`) })

  const impersonate = useMutation({
    mutationFn: () => apiClient.post<{ token: string; expiresIn: number }>(`/admin/users/${id}/impersonate`),
    onSuccess: ({ token }) => {
      // Stash the original token so the admin can come back.
      const prior = localStorage.getItem('discover.jwt')
      if (prior) localStorage.setItem('discover.jwt.admin', prior)
      localStorage.setItem('discover.jwt', token)
      location.href = '/'
    },
  })

  if (user.isLoading) return <p>{t('common.loading')}</p>
  if (user.isError || !user.data) return <p>{t('common.error')}</p>

  const { user: u, memberships } = user.data
  return (
    <div>
      <Link to=".." className="text-xs opacity-70 hover:underline">← {t('admin.user.back')}</Link>
      <h1 className="text-2xl font-semibold mb-1 mt-1">{u.email}</h1>
      <p className="text-xs opacity-50 font-mono mb-6">{u.id}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Field label={t('admin.user.name')} value={u.name ?? '—'} />
        <Field label={t('admin.user.locale')} value={u.locale} />
        <Field label={t('admin.user.verified')} value={u.emailVerified ? '✓' : '—'} />
        <Field label={t('admin.user.default_role')} value={u.defaultRole ?? '—'} />
        <Field label={t('admin.user.lastsignin')} value={u.lastSignInAt ? new Date(u.lastSignInAt).toLocaleString() : '—'} />
        <Field label={t('admin.user.created')} value={new Date(u.createdAt).toLocaleString()} />
      </div>

      <section className="mb-6">
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('admin.user.memberships')}</h2>
        <table className="w-full text-sm border-collapse">
          <thead><tr className="text-left opacity-70"><th className="py-1 pr-4">tenant</th><th className="py-1 pr-4">role</th><th className="py-1 pr-4">plan</th><th className="py-1 pr-4">status</th></tr></thead>
          <tbody>
            {memberships.map((m) => (
              <tr key={m.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="py-1 pr-4">
                  <Link to={`/admin/tenants/${m.tenant.id}`} className="hover:underline">{m.tenant.name}</Link>
                </td>
                <td className="py-1 pr-4">{m.role}</td>
                <td className="py-1 pr-4">{m.tenant.plan}</td>
                <td className="py-1 pr-4">{m.tenant.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('admin.user.impersonate.title')}</h2>
        <p className="text-xs opacity-70 mb-2">{t('admin.user.impersonate.body')}</p>
        <button
          type="button"
          onClick={() => impersonate.mutate()}
          disabled={impersonate.isPending}
          className="px-3 py-1.5 text-sm rounded border"
          style={{ borderColor: 'var(--color-critical)', color: 'var(--color-critical)' }}
        >
          {t('admin.user.impersonate.cta')}
        </button>
      </section>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded border" style={{ borderColor: 'var(--color-border)' }}>
      <div className="text-xs opacity-70">{label}</div>
      <div className="text-sm mt-0.5">{value}</div>
    </div>
  )
}
